const EMBED_PARAM = "embed";
const EMBED_DOMAIN_PARAM = "embed_domain";
/** Full parent origin (scheme + host [+ port]) for postMessage targetOrigin. */
const EMBED_ORIGIN_PARAM = "embed_origin";

let embedReadySent = false;
let resizeDebounceTimer: number | null = null;

export function isEmbedMode(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get(EMBED_PARAM) === "true";
}

function hostnameFromValue(value: string): string | null {
  try {
    if (value.includes("://")) {
      return new URL(value).hostname;
    }
    // hostname or host:port — Apple Pay wants hostname only
    return value.split("/")[0]?.split(":")[0] || null;
  } catch {
    return null;
  }
}

function originFromValue(value: string): string | null {
  try {
    const parsed = value.includes("://")
      ? new URL(value)
      : new URL(`https://${value}`);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.origin;
  } catch {
    return null;
  }
}

export function getEmbedDomain(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const explicit = params.get(EMBED_DOMAIN_PARAM);
  if (explicit) {
    return hostnameFromValue(explicit);
  }

  try {
    if (document.referrer) {
      return new URL(document.referrer).hostname;
    }
  } catch {
    // malformed referrer
  }
  return null;
}

/**
 * Parent window origin for postMessage. Never falls back to "*".
 * Prefers embed_origin from the embed SDK, then referrer / ancestorOrigins.
 */
export function getEmbedParentOrigin(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);

  const explicitOrigin = params.get(EMBED_ORIGIN_PARAM);
  if (explicitOrigin) {
    const origin = originFromValue(explicitOrigin);
    if (origin) return origin;
  }

  const explicitDomain = params.get(EMBED_DOMAIN_PARAM);
  if (explicitDomain) {
    const origin = originFromValue(explicitDomain);
    if (origin) return origin;
  }

  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // malformed referrer
  }

  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) {
      return ancestors[0];
    }
  } catch {
    // ancestorOrigins unsupported or blocked
  }

  return null;
}

export function isInsideIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function postToParent(
  type: string,
  payload?: Record<string, unknown>
): void {
  if (!isInsideIframe()) return;
  const targetOrigin = getEmbedParentOrigin();
  // Refuse to broadcast to every origin (ENG-4771).
  if (!targetOrigin) return;
  try {
    window.parent.postMessage({ type, ...payload }, targetOrigin);
  } catch {
    // cross-origin restriction — silently ignored
  }
}

export function postResize(): void {
  if (resizeDebounceTimer !== null) {
    window.clearTimeout(resizeDebounceTimer);
  }
  resizeDebounceTimer = window.setTimeout(() => {
    resizeDebounceTimer = null;
    const height = document.documentElement.scrollHeight;
    postToParent("stream:resize", { data: { height } });
  }, 80);
}

export function postReady(): void {
  postToParent("stream:ready");
}

/** Idempotent — parent spinner stays until checkout UI is interactive. */
export function signalEmbedReady(): void {
  if (embedReadySent || !isEmbedMode()) return;
  embedReadySent = true;
  postReady();
  postResize();
}

/** Strip HTML splash before React boots so iframe never flashes Stream logo. */
export function initEmbedShell(): void {
  if (!isEmbedMode()) return;
  document.body.classList.remove("page-loading");
  document.body.style.background = "transparent";
  document.documentElement.style.background = "transparent";
  const splash = document.getElementById("splash-screen");
  if (splash) splash.style.display = "none";
}

export function postRedirect(url: string): void {
  postToParent("stream:redirect", { url });
}

/**
 * Moyasar SDK hardcodes `domain_name: window.location.hostname` when calling
 * Apple Pay's validate-merchant endpoint. Inside an iframe the hostname is the
 * billing domain, but Apple checks the top-level origin. This interceptor
 * rewrites `domain_name` in the POST body to the actual parent domain so
 * Apple Pay merchant validation succeeds in embedded checkouts.
 *
 * Call once before loading the Moyasar script; returns a teardown function.
 */
export function patchApplePayDomain(): (() => void) | undefined {
  const embedDomain = getEmbedDomain();
  if (!embedDomain || !isInsideIframe()) return undefined;

  const originalFetch = window.fetch;

  window.fetch = function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ) {
    if (
      init?.method?.toUpperCase() === "POST" &&
      typeof init.body === "string"
    ) {
      try {
        const body = JSON.parse(init.body);
        if (body.validation_url && body.domain_name) {
          body.domain_name = embedDomain;
          init = { ...init, body: JSON.stringify(body) };
        }
      } catch {
        // not JSON — pass through
      }
    }
    return originalFetch.call(window, input, init);
  };

  return () => {
    window.fetch = originalFetch;
  };
}

let resizeObserver: ResizeObserver | null = null;

export function startResizeObserver(): () => void {
  if (resizeObserver) return () => {};

  postResize();

  resizeObserver = new ResizeObserver(() => {
    postResize();
  });
  resizeObserver.observe(document.body);

  return () => {
    resizeObserver?.disconnect();
    resizeObserver = null;
  };
}
